import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';

import { useWritesRefused } from '../components/SuspendedNotice.js';
import { Alert, Card } from '../components/ui.js';
import { fetchClientProfiles, type ClientProfile } from '../lib/ordersApi.js';

/**
 * Projects — ruling A-2.
 *
 * Renamed from "Clients": an agency's clients are their own relationships, and
 * what this holds is a reusable set of order details for a business they order
 * for. The old name also implied a client had to exist before you could order,
 * which is exactly the friction section A removes — so there is no
 * "Add a project" button here any more. Projects are created by ordering.
 */
export function ClientsPage(): React.JSX.Element {
  // Suspended, or an operator viewing read-only - WAIT-B defect 3.
  const { refused: suspended } = useWritesRefused();
  const [projects, setProjects] = useState<ClientProfile[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      const response = await fetchClientProfiles();
      setProjects(response.clientProfiles);
      setError(null);
    } catch {
      setError('We could not load your projects.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Projects</h1>
          <p className="mt-1 text-sm text-fg-muted">
            Reusable order details for the businesses you order for.
          </p>
        </div>
        {suspended ? null : (
        <Link
          to="/orders/new"
          className="shrink-0 rounded-control bg-blue px-4 py-2.5 text-sm font-semibold text-ground transition hover:bg-blue-bright"
        >
          Place an order
        </Link>
        )}
      </div>

      {error ? <Alert>{error}</Alert> : null}

      <Card className="p-0">
        {loading ? (
          <p className="p-6 text-sm text-fg-muted" role="status">
            Loading…
          </p>
        ) : projects.length > 0 ? (
          <ul className="divide-y divide-rule-soft">
            {projects.map((project) => (
              <li key={project.id} className="flex items-center gap-4 px-6 py-4">
                <div className="min-w-0 flex-1">
                  <Link
                    to={`/projects/${project.id}`}
                    className="text-sm font-medium text-fg underline"
                  >
                    {project.businessName}
                  </Link>
                  <p className="mt-0.5 truncate text-sm text-fg-muted">
                    {project.locations.length > 0
                      ? project.locations.join(', ')
                      : 'No locations yet'}
                    {project.services.length > 0 ? ` · ${project.services.length} services` : ''}
                  </p>
                </div>
                {suspended ? null : (
                <Link
                  to={`/orders/new?projectId=${project.id}`}
                  className="shrink-0 text-sm font-medium text-fg underline"
                >
                  Order a build
                </Link>
                )}
              </li>
            ))}
          </ul>
        ) : (
          <div className="p-6">
            <p className="text-sm font-medium text-fg">No projects yet</p>
            <p className="mt-1 text-sm text-fg-muted">
              Place your first order and we will save its details here as a project, ready to
              reuse.
            </p>
            {suspended ? null : (
            <Link
              to="/orders/new"
              className="mt-4 inline-block text-sm font-medium text-fg underline"
            >
              Place an order
            </Link>
            )}
          </div>
        )}
      </Card>
    </div>
  );
}
